import { css, jsx } from '@emotion/react';
import { Icon, Link, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../../hooks/useUniversalNavContext';
import { emitUserInteractionEvent } from '../../../../utils/avroEvents';
import { ArrowLinkIcon } from '../../ArrowLinkIcon';
const Styles = labelStyles('StaticProductRight', {
    root: css({
        display: 'flex',
        flexDirection: 'column',
        marginRight: 1,
        width: 300,
        fontWeight: 'initial',
    }),
    header: css({
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
    }),
    linkHeader: css({
        '&:hover [data-tag="heading"]': {
            color: 'var(--zds-brand-orange, #FF4F00)',
            textDecoration: 'none',
        },
        '&:hover [data-tag="subheading"]': {
            textDecoration: 'underline',
        },
        '&:hover [data-tag="arrow-icon-wrapper"]': {
            marginLeft: 4,
            opacity: 1,
        },
        '&:focus': {
            outline: `var(--zds-border-focus, 2px solid #695BE8)`,
        },
    }),
    heading: css({
        font: `var(--zds-section-header, 600 24px/36px ${Typography.pageHeader1.fontFamily})`,
        color: 'var(--zds-text-stronger, #201515)',
        lineHeight: '24px',
        fontSize: '30px',
        fontWeight: 600,
        letterSpacing: 1,
    }),
    subheading: css({
        font: `var(--zds-small-print-1, 400 13px/20px ${Typography.base.fontFamily})`,
        color: 'var(--zds-gray-warm-9, #413735)',
        display: 'block',
        marginTop: 8,
    }),
    divider: css({
        margin: '16px 0',
        height: 1,
        backgroundColor: 'var(--zds-background-strongest, #ECE9DF)',
    }),
    linkContainer: css({
        display: 'flex',
        columnGap: 6,
        marginBottom: 18,
        textDecoration: 'none',
        color: 'var(--zds-text-stronger, #201515)',
        '&:hover [data-tag="link-heading"]': {
            color: 'var(--zds-brand-orange, #FF4F00)',
            textDecoration: 'none',
        },
        '&:hover [data-tag="link-icon"]': {
            color: 'var(--zds-brand-orange, #FF4F00)',
        },
        '&:hover [data-tag="tag-label"]': {
            textDecoration: 'none',
        },
        '&:hover [data-tag="link-description"]': {
            textDecoration: 'underline',
        },
        '&:hover [data-tag="arrow-icon-wrapper"]': {
            marginLeft: 4,
            opacity: 1,
            color: 'var(--zds-gray-warm-9, #413735)',
        },
        '&:focus': {
            outline: `1px solid var(--zds-ui-primary-strongest, #503EBD)`,
        },
    }),
    linkIcon: css({
        marginTop: 5,
    }),
    linkTextContainer: css({
        display: 'flex',
        flexDirection: 'column',
    }),
    linkHeading: css({
        font: `var(--zds-body-3, 600 14px/24px ${Typography.base.fontFamily})`,
        lineHeight: '30px',
        fontWeight: 500,
    }),
    linkHeadingRow: css({
        display: 'flex',
        alignItems: 'center',
        lineHeight: '30px',
        fontWeight: 500,
    }),
    descriptionWrapper: css({
        font: `var(--zds-small-print-1, 400 13px/20px ${Typography.base.fontFamily})`,
        lineHeight: '20px',
    }),
    linkDescription: css({
        font: `var(--zds-small-print-1, 400 13px/20px ${Typography.base.fontFamily})`,
        color: 'var(--zds-gray-warm-9, #413735)',
        lineHeight: '20px',
    }),
    tagLabel: css({
        font: Typography.base.fontFamily,
        marginLeft: '6px',
        fontSize: '14px',
        lineHeight: '24px',
        fontWeight: 500,
        padding: 'var(--zds-space-0, 0) var(--zds-space-4, 4px)',
        border: '1px solid var(--zds-stroke-default, #b5b2aa)',
        borderRadius: 'var(--zds-radius-small, 3px)',
        backgroundColor: 'transparent',
        textDecoration: 'none',
    }),
});
const MegaMenuProductRight = ({ header, submenu, onCloseMenu, }) => {
    const { getFullyQualifiedURL } = useUniversalNavContext();
    const handleEvent = (text, eventType) => {
        emitUserInteractionEvent({
            event_action: eventType,
            option_selected: `Product tab Right: ${text}`,
        });
        if (eventType === 'click' && onCloseMenu)
            onCloseMenu();
    };
    const Header = () => {
        if (header.href) {
            return (jsx(Link, { color: null, css: [Styles.header, Styles.linkHeader], href: getFullyQualifiedURL(header.href), target: header.target, onClick: () => handleEvent('Early Access Program heading', 'click'), onMouseEnter: () => handleEvent('Early Access Program heading', 'hover') },
                jsx("span", { css: Styles.heading, "data-tag": "heading", "data-zds": true }, header.heading),
                header.subheading && (jsx("div", { "data-zds": true },
                    jsx("span", { css: Styles.subheading, "data-tag": "subheading", "data-zds": true },
                        header.subheading,
                        jsx(ArrowLinkIcon, null))))));
        }
        else {
            return (jsx("header", { css: Styles.header, "data-zds": true },
                jsx("span", { css: Styles.heading, "data-tag": "heading", "data-zds": true }, header.heading),
                jsx("div", { "data-zds": true },
                    jsx("span", { css: Styles.subheading, "data-tag": "subheading", "data-zds": true }, header.subheading))));
        }
    };
    return (jsx("div", { css: Styles.root, "data-zds": true },
        jsx(Header, null),
        jsx("hr", { css: Styles.divider, "data-zds": true }),
        jsx("ul", { "data-zds": true }, submenu.map((link) => (jsx("li", { key: link.text, "data-zds": true },
            jsx(Link, { color: null, css: Styles.linkContainer, href: getFullyQualifiedURL(link.href), target: link.target, onClick: () => handleEvent(link.text, 'click'), onMouseEnter: () => handleEvent(link.text, 'hover') },
                jsx("span", { css: Styles.linkIcon, "data-tag": "link-icon", "data-zds": true },
                    jsx(Icon, { name: link.icon, size: 18 })),
                jsx("div", { css: Styles.linkTextContainer, "data-zds": true },
                    jsx("div", { css: Styles.linkHeadingRow, "data-zds": true },
                        jsx("span", { css: Styles.linkHeading, "data-tag": "link-heading", "data-zds": true }, link.text),
                        link.tag && (jsx("span", { css: Styles.tagLabel, "data-tag": "tag-label", "data-zds": true }, link.tag))),
                    jsx("div", { css: Styles.descriptionWrapper, "data-zds": true },
                        jsx("span", { css: Styles.linkDescription, "data-tag": "link-description", "data-zds": true }, link.description),
                        jsx(ArrowLinkIcon, null))))))))));
};
export default MegaMenuProductRight;
//# sourceMappingURL=MegaMenuProductRight.js.map
// @zapier/universal-layout@9.12.4 (a02ac1bd0)
